module.exports = function HelpCenter(ProxyService, $http) {
    const ZENDESK_BASE_URL = window.config.ZENDESK_BASE_URL;
    const HELP_CENTER_SEARCH_V2 = window.config.HELP_CENTER_SEARCH_V2;
    const HELP_CENTER_CATEGORIES = window.config.HELP_CENTER_CATEGORIES;
    const HELP_CENTER_CATEGORY_SECTIONS = window.config.HELP_CENTER_CATEGORY_SECTIONS;
    const HELP_CENTER_SECTION_ARTICLES = window.config.HELP_CENTER_SECTION_ARTICLES;
    const HELP_CENTER_ARTICLES = window.config.HELP_CENTER_ARTICLES;
    const SERVICE_CALLOUT_SECTION_ID = window.config.SERVICE_CALLOUT_SECTION_ID;
    const CHATSERVER_BASE_URL = window.config.CHATSERVER_BASE_URL;
    const ARTICLE_LIST_BWS_API = window.config.ARTICLE_LIST_BWS_API;
    const CHATSERVER_JWT = window.config.CHATSERVER_JWT;

    // Initialize properties
    this.proxyService = ProxyService;
    this.$http = $http;
    this.zendeskSearchURL = `${ZENDESK_BASE_URL}${HELP_CENTER_SEARCH_V2}`;
    this.zendeskCategoriesURL = `${ZENDESK_BASE_URL}${HELP_CENTER_CATEGORIES}`;
    this.zendeskSectionsURL = `${ZENDESK_BASE_URL}${HELP_CENTER_CATEGORY_SECTIONS}`;
    this.zendeskArticlesURL = `${ZENDESK_BASE_URL}${HELP_CENTER_SECTION_ARTICLES}`;
    this.zendeskArticleDetailsUrl = `${ZENDESK_BASE_URL}${HELP_CENTER_ARTICLES}`;
    this.articleListBWSAPI = `${CHATSERVER_BASE_URL}${ARTICLE_LIST_BWS_API}`;

    // Function to search help center articles
    this.getHelpCenterSearch = function (query) {
        return this.$http.get(this.zendeskSearchURL + query)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
                return Promise.reject(error);
            });
    };

    this.getCategories = function () {
        return this.$http.get(this.zendeskCategoriesURL)
            .then((response) => {
                return response.data.categories;
            })
            .catch((error) => {
                console.error("Error fetching categories:", error);
                return Promise.reject(error);
            });
    };

    this.getSectionsByCategoryId = function (categoryId) {
        const url = this.zendeskSectionsURL.replace('{categoryId}', categoryId);
        return this.$http.get(url)
            .then((response) => {
                return response.data.sections;
            })
            .catch((error) => {
                console.error("Error fetching sections:", error);
                return Promise.reject(error);
            });
    };

    this.getArticlesBySectionId = function (sectionId) {
        const url = this.zendeskArticlesURL.replace('{sectionId}', sectionId);
        return this.$http.get(url)
            .then((response) => {
                return response.data.articles;
            })
            .catch((error) => {
                console.error("Error fetching articles:", error);
                return Promise.reject(error);
            });
    };

    this.getArticleDetails = function (articleId) {
        const url = this.zendeskArticleDetailsUrl.replace('{articleId}', articleId);
        return this.$http.get(url)
            .then((response) => {
                return response.data.article;
            })
            .catch((error) => {
                console.error("Error fetching article details:", error);
                return Promise.reject(error);
            });
    };

    this.getArticleSummary = function (articleId) {
        return this.getArticleDetails(articleId)
            .then(function (article) {
                if (!article) return null;

                // Create a temporary div to parse HTML and extract text
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = article.body || '';
                const plainText = tempDiv.innerText;

                // Split into words
                const words = plainText.trim().split(/\s+/);

                // Take the first 60 words or fewer if the text is shorter
                const summaryWords = words.slice(0, 60);
                const summary = summaryWords.join(' ').trim();

                return {
                    id: article.id,
                    title: article.title,
                    summary: summary + (words.length > 60 ? '...' : ''),
                    fullUrl: article.html_url
                };
            })
            .catch(function (error) {
                console.error("Error getting article summary:", error);
                return null;
            });
    };

    this.getLatestServiceCalloutArticle = function () {
        return this.getArticlesBySectionId(SERVICE_CALLOUT_SECTION_ID)
            .then(function (articles) {
                if (!articles || articles.length === 0) {
                    return null;
                }

                const promotedArticles = articles.filter(article => article.promoted);
                if (promotedArticles.length === 0) {
                    return null;
                }

                promotedArticles.sort((a, b) =>
                    new Date(b.created_at) - new Date(a.created_at)
                );
                return this.getArticleDetails(promotedArticles[0].id);
            }.bind(this))
            .then(function (articleDetails) {
                if (!articleDetails) {
                    return null;
                }

                try {
                    // Create a temporary div to parse HTML and extract text
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = articleDetails.body || '';
                    const plainTextBody = tempDiv.innerText;

                    const words = plainTextBody.trim().split(/\s+/).filter(word => word.length > 0);
                    const wordCount = Math.min(words.length, 35);
                    const truncatedBody = words.slice(0, wordCount).join(' ');

                    return {
                        id: articleDetails.id,
                        title: articleDetails.title,
                        updated_at: articleDetails.updated_at,
                        truncatedBody: truncatedBody + (wordCount < words.length ? '...' : '')
                    };
                } catch (error) {
                    console.error("Error processing article body:", error);
                    return null;
                }
            })
            .catch(function (error) {
                console.error("Error fetching latest article:", error);
                return null;
            });
    };

    this.getBwsArticles = function () {
        const headers = { 'Ocp-Apim-Subscription-Key': CHATSERVER_JWT };
        return this.$http.get(this.articleListBWSAPI, { headers: headers })
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.error("Error fetching BWS articles:", error);
                return null;
            });
    };

    this.getCategorySectionMap = function () {
        const cachedMap = sessionStorage.getItem("category-section-map");
        if (cachedMap) {
            return Promise.resolve(JSON.parse(cachedMap));
        }
        return this.buildCategorySectionMap();
    };

    this.buildCategorySectionMap = function () {
        return this.getCategories().then(categories => {
            const categoryPromises = categories.map(category => {
                return this.getSectionsByCategoryId(category.id).then(sections => {
                    return {
                        categoryId: category.id,
                        sectionData: sections.map(section => ({
                            sectionId: section.id,
                            sectionName: section.name,
                            categoryName: category.name
                        }))
                    };
                });
            });

            return Promise.all(categoryPromises).then(categorySectionsMap => {
                sessionStorage.setItem("category-section-map", JSON.stringify(categorySectionsMap));
                return categorySectionsMap;
            });
        });
    };

    this.getCategoryAndSectionInfo = function (sectionId) {
        return this.getCategorySectionMap().then(categorySectionsMap => {
            for (let categorySection of categorySectionsMap) {
                for (let sectionDetails of categorySection.sectionData) {
                    if (sectionDetails.sectionId === sectionId) {
                        return {
                            categoryId: categorySection.categoryId,
                            categoryName: sectionDetails.categoryName,
                            sectionName: sectionDetails.sectionName
                        };
                    }
                }
            }
            return null;
        });
    };
};


/** WEBPACK FOOTER **
 ** ./wowapp/src/shop/components/help-center/help-center-service.js
 **/